/**
 * @fileoverview Selections Print Layout Component
 *
 * Renders a hidden, print-optimized layout of selections data that becomes
 * visible only during printing (controlled by SelectionsPrint.css).
 *
 * Includes:
 * - Page 1: Overview selections organized by section (Exterior, Interior, Trim, Interior Features)
 * - Page 2: Paint selections table (only rendered if paint selections exist)
 *
 * Used by both client portal (ClientSelections) and admin portal (HomeSelections).
 * Must be placed inside the main page wrapper so @media print rules can target it.
 *
 * Location: src/components/SelectionsPrintLayout.js
 *
 * @version 1.0.0
 */

import React from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import { buildOverviewRows } from "../utils/selectionsExport";
import "../styles/SelectionsPrint.css";

/**
 * Renders a single field row for the print layout
 * @param {Object} props
 * @param {string} props.label - Field display name
 * @param {string} props.value - Display value
 * @returns {JSX.Element}
 */
const PrintField = ({ label, value, multiline = false }) => {
  const isPending = value === "Selection Needed";

  return (
    <div
      className={`sp-print-field${multiline ? " sp-print-field--multiline" : ""}`}
    >
      <span className="sp-print-field-label">{label}</span>
      <span
        className={`sp-print-field-value${isPending ? " sp-pending" : ""}${
          multiline ? " sp-print-field-value--multiline" : ""
        }`}
      >
        {isPending ? (
          <FaExclamationTriangle aria-label="Selection needed" />
        ) : (
          value
        )}
      </span>
    </div>
  );
};

/**
 * Renders a print section (Exterior, Interior, etc.) with its fields
 * @param {Object} props
 * @param {string} props.title - Section heading
 * @param {Array<{label: string, value: string}>} props.fields - Section fields
 * @returns {JSX.Element}
 */
const PrintSection = ({ title, fields, multiline = false }) => {
  if (!fields || fields.length === 0) return null;

  return (
    <div className="sp-print-section">
      <h3 className="sp-print-section-title">{title}</h3>
      <div
        className={`sp-print-fields${multiline ? " sp-print-fields--notes" : ""}`}
      >
        {fields.map((field, index) => (
          <PrintField
            key={`${title}-${index}`}
            label={field.label}
            value={field.value}
            multiline={multiline}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * SelectionsPrintLayout Component
 *
 * Hidden on screen, visible only when printing. Renders a clean,
 * formatted version of all selections data including paint selections.
 *
 * @param {Object} props
 * @param {Object} props.selections - The clientSelections data object
 * @param {Array} props.paintSelections - Array of paint selection objects
 * @param {string} [props.projectName] - Project name for the header
 * @returns {JSX.Element}
 */
const SelectionsPrintLayout = ({
  selections,
  paintSelections,
  projectName,
}) => {
  if (!selections) return null;

  // Build all overview rows and group by section
  const allRows = buildOverviewRows(selections);
  const sections = {};
  allRows.forEach((row) => {
    if (!sections[row.section]) {
      sections[row.section] = [];
    }
    sections[row.section].push({ label: row.label, value: row.value });
  });

  // Section display order (page 1). Additional Selections / Notes is
  // rendered on its own page below, so it is excluded here.
  const sectionOrder = ["Exterior", "Interior", "Trim", "Interior Features"];

  // Notes section rows (only present when the field is non-empty)
  const notesRows = sections["Additional Selections / Notes"];

  // Check if paint data exists
  const hasPaint = paintSelections && paintSelections.length > 0;

  // Generate the current date for print header
  const printDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "America/New_York",
  });

  return (
    <div className="sp-print-layout-wrapper">
      {/* Repeating page footer — prints at bottom of every page */}
      <div className="sp-page-footer">
        <div className="sp-signature-line">
          <span className="sp-sig-label">Client Initial:</span>
          <span className="sp-sig-blank"></span>
        </div>
        <div className="sp-signature-line">
          <span className="sp-sig-label">Date:</span>
          <span className="sp-sig-blank"></span>
        </div>
      </div>

      {/* Page 1: Overview */}
      <div className="sp-print-overview">
        <div className="sp-print-header">
          <h1>{projectName} Selections</h1>
          <p>
            <strong>
              Due Date:{" "}
              {new Date(selections.selectionsDueDate).toLocaleDateString(
                "en-US",
                {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  timeZone: "America/New_York",
                },
              )}
            </strong>{" "}
            | Printed {printDate}
          </p>
        </div>

        {sectionOrder.map((sectionName) =>
          sections[sectionName] ? (
            <PrintSection
              key={sectionName}
              title={sectionName}
              fields={sections[sectionName]}
            />
          ) : null,
        )}
      </div>

      {/* Page 2 (if notes exist): Additional Selections / Notes */}
      {notesRows && (
        <div className="sp-print-notes">
          <div className="sp-print-header">
            <h1>{projectName} Selections</h1>
            {selections.selectionsDueDate && (
              <p>
                <strong>
                  Due Date:{" "}
                  {new Date(selections.selectionsDueDate).toLocaleDateString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      timeZone: "America/New_York",
                    },
                  )}
                </strong>
              </p>
            )}
            <p>Printed {printDate}</p>
          </div>
          <PrintSection
            title="Additional Selections / Notes"
            fields={notesRows}
            multiline
          />
        </div>
      )}

      {/* Paint Selections page (only if data exists) */}
      {hasPaint && (
        <div className="sp-print-paint">
          <h2 className="sp-print-paint-title">
            {projectName} Paint Selections
          </h2>
          <table className="sp-print-paint-table">
            <thead>
              <tr>
                <th>Area(s)</th>
                <th>Paint Code/Name</th>
                <th>Sheen</th>
                <th>Texture</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {paintSelections.map((paint) => {
                const areas = Array.isArray(paint.areas)
                  ? paint.areas.join(", ")
                  : paint.areas || "Unspecified";

                return (
                  <tr key={paint.id}>
                    <td>{areas}</td>
                    <td>{paint.paintSelection || "—"}</td>
                    <td>{paint.sheen || "—"}</td>
                    <td>{paint.texture || "—"}</td>
                    <td>{paint.locationNotes || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SelectionsPrintLayout;
